import { Grid, GridRow } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { _ClassesQuery } from '../../../types/planung-graphql-client-defs';
import { showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useDeputateLessons } from '../hooks/useDeputateLessons';
import { LessonFormType } from '../types';
import { EpochBasicsForm } from './Basics/EpochBasicsForm';
import { LessonClassesForm } from './Classes/LessonClassesForm';
import { EpochLessonUnitForm } from './LessonUnits/EpochLessonUnitForm';
import { epochSchema } from './validation/schema';

type EpochPlanFormProps = {
  lessonUuid?: string | null;
  classUuid?: string | null;
  defaultInitialValues: Partial<LessonFormType>;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
  classData?: Pick<_ClassesQuery, 'classes'>['classes'];
};

export const DeputateEpochForm = observer(
  ({ lessonUuid, defaultInitialValues, onLoad, onClose, classData }: EpochPlanFormProps) => {
    const { t } = useTranslation();
    const { pimAuthClaims } = useAuthClaims();
    const schoolYear = useUserConfigContext().selectedSchoolYear;

    const { createLesson, updateLesson, queryLessonsData, lessonsData } = useDeputateLessons({
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      schoolYearUuid: schoolYear?.uuid ?? '',
    });

    const lesson = queryLessonsData?.lessons.find((lesson) => {
      return lesson.uuid === lessonUuid;
    });

    const status = lessonsData.find((lesson) => {
      return lesson.uuid === lessonUuid;
    })?.editStatus;

    const { subjectContainerData } = useLoadBasicData({ pause: false });

    const initialValues: LessonFormType = lesson
      ? {
          uuid: lesson.uuid,
          comment: lesson.comment,
          lessonUnit: lesson.lessonUnitConnection.edges.map(({ node }) => {
            return { ...node, lessonUnitType: node.subjectContainer?.uuid ? 'epoch' : 'subjectHour' };
          }),
          lessonClasses: lesson.lessonClassesConnection.edges
            .sort((a, b) => ((a.node.class.grade ?? -1) > (b.node.class.grade ?? -1) ? 1 : -1))
            .map((lc) => {
              return { ...lc.node };
            }),
          subject: lesson.subject,
          subTitle: lesson.subTitle,
          validGrades: subjectContainerData?.subjectContainers?.find((sc) => sc.uuid === lesson.subject.uuid)?.grades,
        }
      : { ...defaultInitialValues };

    const handleSubmit = async (values: LessonFormType, formikHelpers: FormikHelpers<LessonFormType>) => {
      onLoad(true);
      if (values.validGrades) {
        const grades = values.lessonClasses?.map((lc) => {
          const c = classData?.find((c) => c.uuid === lc.class.uuid);
          return c?.grade;
        });
        if (grades) {
          const valid = grades.every((g) => (g ? values.validGrades?.includes(g) : false));
          if (!valid) {
            formikHelpers.setFieldError('lessonClasses', t('lessons.errors.differentGrades'));
            onLoad(false);
            return;
          }
        }
      }
      if (!lessonUuid) {
        const results = await createLesson(values);

        if (results.find((r) => r.error)) {
          showUserDbErrorToast();
        } else {
          showSuccessUpdateToast();
        }
      } else {
        const { error } = await updateLesson(values, lessonUuid);
        if (error) {
          showUserDbErrorToast();
        } else {
          showSuccessUpdateToast();
        }
      }

      onLoad(false);
      formikHelpers.resetForm();
      onClose();
    };

    return (
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={epochSchema}>
        {({ values, resetForm, errors, dirty }) => {
          return (
            <Form>
              <Grid useFormGap>
                <GridRow>
                  <EpochBasicsForm status={status} preFilterSubjectContainers={true} />
                </GridRow>

                <GridRow headline={t('classes.title', { count: 2 })}>
                  <LessonClassesForm
                    disabled={status === 'blocked'}
                    filter={(c) => {
                      return c.grade ? (values.validGrades?.includes(c.grade) ?? true) : false;
                    }}
                  />
                </GridRow>

                <GridRow headline={t('subject.subjectHour')}>
                  <EpochLessonUnitForm disabled={status === 'blocked'} key={values.subject?.uuid} />
                </GridRow>
              </Grid>

              <ModalBottomButtons
                submitButton={{
                  disabled: !dirty,
                }}
                closeButton={{
                  callback: () => {
                    resetForm();
                    onClose();
                  },
                }}
                errors={errors}
              />
            </Form>
          );
        }}
      </Formik>
    );
  },
);
